import React, { useState, useRef } from 'react';
import { Plus, Edit2, Trash2, User, Upload, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamMemberModal = ({ member, onSave, onClose }) => {
  const [form, setForm] = useState(member || {
    name: '', role: '', bio: '', image: '', linkedin: '', email: '', order: 0, visible: true
  });
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await uploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = async (e) => {
    if (e.target.files && e.target.files[0]) {
      await uploadFile(e.target.files[0]);
    }
  };

  const uploadFile = async (file) => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file (PNG, JPG, GIF, WebP)');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const password = localStorage.getItem('adminPassword');
      const response = await fetch(`${API_URL}/api/upload/team-photo?password=${password}`, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setForm({...form, image: data.image_url});
        toast.success('Photo uploaded successfully');
      } else {
        toast.error('Failed to upload photo');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative bg-[#0a1a14] border border-white/10 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg text-white mb-4">{member ? 'Edit Team Member' : 'Add Team Member'}</h3>
        <div className="space-y-4">
          {/* Photo Upload */}
          <div>
            <label className="text-white/50 text-sm block mb-1">Photo</label>
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-full bg-white/5 border border-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center">
                {form.image ? (
                  <img src={form.image} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                ) : (
                  <User className="w-8 h-8 text-white/30" />
                )}
              </div>
              <div
                className={`flex-1 border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer
                  ${dragActive ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-white/20 hover:border-white/40'}
                  ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload className={`w-5 h-5 mx-auto mb-1 ${dragActive ? 'text-[#D4AF37]' : 'text-white/30'}`} />
                {uploading ? (
                  <p className="text-white/50 text-xs">Uploading...</p>
                ) : (
                  <p className="text-white/40 text-xs">Drop photo or click to browse</p>
                )}
              </div>
            </div>
            <div className="mt-2">
              <Input 
                value={form.image} 
                onChange={(e) => setForm({...form, image: e.target.value})} 
                className="bg-white/5 border-white/10 text-white text-sm" 
                placeholder="Or paste image URL..."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-sm block mb-1">Name *</label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="text-white/50 text-sm block mb-1">Role *</label>
              <Input value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="bg-white/5 border-white/10 text-white" />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-sm block mb-1">Email *</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <label className="text-white/50 text-sm block mb-1">Sort Order</label>
              <Input 
                type="number" 
                value={form.order} 
                onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} 
                className="bg-white/5 border-white/10 text-white" 
                placeholder="0"
              />
            </div>
          </div>

          {/* Visibility Toggle */}
          <div>
            <label className="text-white/50 text-sm block mb-1">Visibility</label>
            <button
              type="button"
              onClick={() => setForm({...form, visible: form.visible === false ? true : false})}
              className={`w-full h-10 rounded-md border transition-colors flex items-center justify-center gap-2 text-sm
                ${form.visible !== false
                  ? 'bg-green-500/20 border-green-500/40 text-green-400' 
                  : 'bg-white/5 border-white/10 text-white/40'}`}
            >
              {form.visible !== false ? (
                <><CheckCircle className="w-4 h-4" /> Visible on Website</>
              ) : (
                <><AlertCircle className="w-4 h-4" /> Hidden from Website</>
              )}
            </button>
            <p className="text-white/30 text-xs mt-1">Hidden members won't appear on the public website</p>
          </div>

          <div>
            <label className="text-white/50 text-sm block mb-1">Bio</label>
            <textarea 
              value={form.bio} 
              onChange={(e) => setForm({...form, bio: e.target.value})} 
              className="w-full bg-white/5 border border-white/10 text-white rounded-md p-2 min-h-[80px]"
            />
          </div>
          <div>
            <label className="text-white/50 text-sm block mb-1">LinkedIn URL</label>
            <Input value={form.linkedin} onChange={(e) => setForm({...form, linkedin: e.target.value})} className="bg-white/5 border-white/10 text-white" placeholder="https://linkedin.com/in/..." />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
          <Button 
            onClick={() => onSave({...form, id: member?.id})} 
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
            disabled={!form.name || !form.role || !form.email || uploading}
          >
            {member ? 'Update' : 'Add'} Member
          </Button>
        </div>
      </div>
    </div>
  );
};

export const TeamTab = ({ teamMembers, sectionSettings, toggleTeamSection, saveTeamMember, deleteTeamMember }) => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  return (
    <>
      <div className="space-y-4">
        {/* Section Toggle */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Team Section Visibility</h4>
              <p className="text-white/40 text-sm">Show or hide the Leadership Team section on the public website</p>
            </div>
            <button
              onClick={toggleTeamSection}
              data-testid="team-section-toggle"
              className={`relative w-14 h-7 rounded-full transition-colors ${
                sectionSettings.team_section_enabled ? 'bg-green-500' : 'bg-white/20'
              }`}
            >
              <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                sectionSettings.team_section_enabled ? 'left-8' : 'left-1'
              }`} />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg text-white">Team Members</h3>
            <p className="text-white/40 text-sm">Manage your leadership team. Lower order numbers appear first.</p>
          </div>
          <Button 
            onClick={() => { setEditing(null); setShowModal(true); }}
            className="bg-[#D4AF37] text-black hover:bg-[#B8941E]"
            data-testid="add-team-member-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Member
          </Button>
        </div>
        {teamMembers.length === 0 ? (
          <div className="text-center text-white/50 py-8">No team members yet. Add your first team member!</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {teamMembers.map((member) => (
              <div 
                key={member.id} 
                className={`bg-white/5 border rounded-lg p-4 group relative transition-all
                  ${member.visible === false ? 'border-white/5 opacity-60' : 'border-white/10'}`}
                data-testid={`team-card-${member.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#D4AF37]/20 overflow-hidden flex-shrink-0 flex items-center justify-center">
                    {member.image ? (
                      <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-7 h-7 text-[#D4AF37]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium truncate">{member.name}</h4>
                    <p className="text-[#D4AF37] text-sm">{member.role}</p>
                    <p className="text-white/50 text-xs mt-1">{member.email}</p>
                    <p className="text-white/30 text-xs mt-1">Order: {member.order || 0}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={() => { setEditing(member); setShowModal(true); }} 
                      className="p-1.5 hover:bg-white/10 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`edit-team-${member.id}`}
                    >
                      <Edit2 className="w-4 h-4 text-white/60" />
                    </button>
                    <button 
                      onClick={() => deleteTeamMember(member.id)} 
                      className="p-1.5 hover:bg-red-500/20 rounded text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-team-${member.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* Inline Enable/Disable Toggle */}
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    {member.visible !== false ? (
                      <Eye className="w-3.5 h-3.5 text-green-400" />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5 text-white/30" />
                    )}
                    <span className={`text-xs ${member.visible !== false ? 'text-green-400/80' : 'text-white/30'}`}>
                      {member.visible !== false ? 'Visible on site' : 'Hidden from site'}
                    </span>
                  </div>
                  <button
                    onClick={() => saveTeamMember({ ...member, visible: member.visible === false ? true : false })}
                    data-testid={`toggle-visibility-${member.id}`}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${
                      member.visible !== false ? 'bg-green-500/70' : 'bg-white/15'
                    }`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${
                      member.visible !== false ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <TeamMemberModal
          member={editing}
          onSave={(m) => { saveTeamMember(m); setShowModal(false); setEditing(null); }}
          onClose={() => { setShowModal(false); setEditing(null); }}
        />
      )}
    </>
  );
};
